import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import CourseProgressBar from './CourseProgressBar';
import '../styles/components/_timeline.scss';
import {
  BarChart2Icon,
  BoxIcon,
  CheckIcon,
  CircleIcon,
  ClipboardIcon,
  ExpandLessIcon,
  ExpandMoreIcon,
  InfoIcon,
  LockIcon,
  WarningIcon,
  WorkshopIcon
} from '../utils/icons';
import { getDisplayLocale } from '../utils/i18n';

interface TimelineItem {
  id: string;
  type: 'capsule' | 'quiz' | 'workshop' | 'certificate';
  title: string;
  description?: string;
  duration?: string;
  date?: string;
  isCompleted: boolean;
  isActive: boolean;
  isLocked: boolean;
  unlockDate?: string;
}

interface Chapter {
  id: string;
  title: string;
  items: TimelineItem[];
  completedCount: number;
  totalCount: number;
  isLocked: boolean;
  unlockDate?: string;
  deadline?: string;
  remainingCount?: number;
}

export interface AssessmentTimelineStep {
  /** initial = diagnostic ; final = mesure d'impact obligatoire ; followup = suivi M+ */
  kind: 'initial' | 'final' | 'followup';
  /** Requis si kind === 'followup' */
  followupId?: string;
  title: string;
  isCompleted: boolean;
  /** Verrouillé tant que les conditions d'éligibilité ne sont pas réunies. */
  isLocked: boolean;
  /** L'apprenant est actuellement en train de faire ce questionnaire. */
  isActive: boolean;
  lockedHint?: string;
  /** Label court expliquant la règle de déblocage (mesure d’impact). */
  unlockConditionHint?: string;
}

interface TimelineProps {
  courseTitle: string;
  progress: number;
  chapters: Chapter[];
  onItemClick?: (itemId: string, chapterId: string) => void;
  /** Étapes de diagnostic & mesure d'impact (s'affichent en haut/bas de la timeline). */
  assessmentDiagnostic?: AssessmentTimelineStep | null;
  assessmentFinal?: AssessmentTimelineStep | null;
  /** Suivis d'impact M+ (après la mesure finale), dans l'ordre pédagogique. */
  assessmentFollowups?: AssessmentTimelineStep[];
  onAssessmentClick?: (kind: 'initial' | 'final' | 'followup', followupId?: string) => void;
}

/** Chapitre « en cours » : celui avec l’item actif, sinon premier déblocable avec travail à faire, sinon premier ouvert. */
function getDefaultExpandedChapterId(list: Chapter[]): string | null {
  const unlocked = list.filter((c) => !c.isLocked);

  const withActive = unlocked.find((ch) => ch.items?.some((it) => it.isActive));
  if (withActive) return withActive.id;

  const withTodo = unlocked.find((ch) =>
    ch.items?.some((it) => !it.isCompleted && !it.isLocked)
  );
  if (withTodo) return withTodo.id;

  return unlocked[0]?.id ?? null;
}

const Timeline: React.FC<TimelineProps> = ({
  courseTitle,
  progress,
  chapters,
  onItemClick,
  assessmentDiagnostic,
  assessmentFinal,
  assessmentFollowups = [],
  onAssessmentClick,
}) => {
  const { t, i18n } = useTranslation();

  /** Surcharges explicites (clic utilisateur). Si absent, voir `defaultExpandedChapterId`. */
  const [expandedChapters, setExpandedChapters] = React.useState<Record<string, boolean>>({});

  const defaultExpandedChapterId = React.useMemo(
    () => getDefaultExpandedChapterId(chapters),
    [chapters]
  );

  const chapterIsExpanded = React.useCallback(
    (chapter: Chapter): boolean => {
      if (chapter.isLocked) return false;
      const override = expandedChapters[chapter.id];
      if (override !== undefined) return override;
      return chapter.id === defaultExpandedChapterId;
    },
    [expandedChapters, defaultExpandedChapterId]
  );

  const toggleChapter = (chapterId: string, isLocked: boolean) => {
    if (isLocked) return;

    const chapter = chapters.find((c) => c.id === chapterId);
    if (!chapter) return;

    const currentlyExpanded = chapterIsExpanded(chapter);
    setExpandedChapters((prev) => ({
      ...prev,
      [chapterId]: !currentlyExpanded,
    }));
  };
  const getItemIcon = (item: TimelineItem) => {
    if (item.isLocked) {
      return <LockIcon size={20} color="rgba(255, 255, 255, 0.5)" className="timeline__item-icon timeline__item-icon--locked" />;
    }
    if (item.isCompleted) {
      // Rond vert avec check pour les items complétés
      return (
        <Box className="timeline__item-icon timeline__item-icon--completed">
          <CheckIcon size={14} color="var(--brand-on-primary)" strokeWidth={3} />
        </Box>
      );
    }
    if (item.isActive) {
      return <CircleIcon size={20} color="var(--brand-primary)" className="timeline__item-icon timeline__item-icon--active" />;
    }
    return <CircleIcon size={20} color="rgba(255, 255, 255, 0.5)" className="timeline__item-icon timeline__item-icon--default" />;
  };


  const getItemTypeIcon = (type: string, color: string) => {
    switch (type) {
      case 'capsule':
        return <BoxIcon size={14} color={color} />;
      case 'quiz':
        return <ClipboardIcon size={14} color={color} />;
      case 'workshop':
        return <WorkshopIcon size={14} color={color} />;
      default:
        return null;
    }
  };

  const formatWorkshopStart = (date?: string, startTime?: string): string => {
    if (!date) return '';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hhmm = startTime || `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    return `${dd}/${mm} ${hhmm}`;
  };

  const getItemLabel = (item: TimelineItem): string => {
    if (item.type === 'workshop') {
      const workshopStart = formatWorkshopStart(item.date, (item as any).startTime);
      if (item.description && workshopStart) return `${item.description} - ${workshopStart}`;
      return item.description || workshopStart || '';
    }
    // quiz + capsule: label = description
    return item.description || '';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDeadline = (deadline: string): string => {
    const date = new Date(deadline);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit'
    });
  };

  const formatUnlockDate = (unlockDate: string): string => {
    const date = new Date(unlockDate);
    const lang = i18n.language.split('-')[0] || 'fr';
    const locale = getDisplayLocale(lang);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit'
    });
  };

  /** Rend un step assessment (diagnostic ou mesure d'impact) dans la timeline interne. */
  const renderAssessmentStep = (step: AssessmentTimelineStep) => {
    const Icon = BarChart2Icon;
    // Pas de clic si verrouillé, déjà terminé, ou handler absent (inutile de rouvrir un questionnaire soumis).
    const clickable =
      !step.isLocked &&
      !step.isCompleted &&
      !!onAssessmentClick &&
      (step.kind !== 'followup' || !!step.followupId);
    const stateClass = step.isLocked
      ? 'timeline__chapter--locked'
      : step.isCompleted
        ? 'timeline__chapter--completed'
        : '';

    let rightIcon: React.ReactNode;
    if (step.isLocked) {
      rightIcon = <LockIcon size={22} color="rgba(255, 255, 255, 0.5)" />;
    } else if (step.isCompleted) {
      rightIcon = (
        <Box className="timeline__item-icon timeline__item-icon--completed">
          <CheckIcon size={14} color="var(--brand-on-primary)" strokeWidth={3} />
        </Box>
      );
    } else if (step.isActive) {
      rightIcon = <CircleIcon size={20} color="var(--brand-primary)" />;
    } else {
      rightIcon = <CircleIcon size={20} color="rgba(255, 255, 255, 0.5)" />;
    }

    return (
      <div className={`timeline__chapter timeline__assessment-step ${stateClass}`}>
        <Box
          onClick={
            clickable
              ? () => onAssessmentClick!(step.kind, step.followupId)
              : undefined
          }
          className={`timeline__chapter-header ${step.isLocked ? 'timeline__chapter-header--locked' : ''
            }`}
          sx={step.isCompleted ? { cursor: 'default' } : undefined}
        >
          <Icon
            size={22}
            color={step.isActive ? 'var(--brand-primary)' : 'rgba(255,255,255,0.85)'}
          />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.25,
              mx: '5px',
            }}
          >
            <p
              className="timeline__chapter-title"
              style={{
                color: step.isActive ? 'var(--brand-primary)' : '#ffffff',
                flex: 'unset',
                margin: 0,
              }}
            >
              {step.title}
            </p>
          </Box>
          <Box className="timeline__chapter-progress">{rightIcon}</Box>
        </Box>
        {step.isLocked && step.lockedHint && (
          <Box className="timeline__chapter-unlock-message">
            <InfoIcon size={16} color="#A0A0A0" />
            <Typography
              variant="body2"
              className="timeline__chapter-unlock-text"
            >
              {step.lockedHint}
            </Typography>
          </Box>
        )}
      </div>
    );
  };

  return (
    <Box className="timeline">
      <Box className="timeline__header">
        {/* Course Title */}
        <p
          className="timeline__title"
        >
          {courseTitle}
        </p>

        {/* Progress Bar */}
        <div className="timeline__progress">
          <CourseProgressBar progress={progress} size="sidebar" />
        </div>
      </Box>
      {/* Chapters List */}
      <Box className="timeline__chapters">
        {/* Étape diagnostic (avant les chapitres) */}
        {assessmentDiagnostic && renderAssessmentStep(assessmentDiagnostic)}

        {chapters.map((chapter, chapterIndex) => {
          const isExpanded = chapterIsExpanded(chapter);
          const isChapterComplete = chapter.completedCount === chapter.totalCount && chapter.totalCount > 0;
          const chapterProgress = chapter.totalCount > 0 ? (chapter.completedCount / chapter.totalCount) * 100 : 0;

          return (
            <div key={chapter.id} className={`timeline__chapter ${chapter.isLocked ? 'timeline__chapter--locked' : ''}`}>
              {/* Chapter Header - Clickable */}
              <Box
                onClick={() => toggleChapter(chapter.id, chapter.isLocked)}
                className={`timeline__chapter-header ${chapter.isLocked ? 'timeline__chapter-header--locked' : ''}`}
              >
                {/* Expand/Collapse Icon - masqué si locké */}
                {!chapter.isLocked && (
                  <>
                    {isExpanded ? (
                      <ExpandLessIcon size={24} color="rgb(255, 255, 255)" className="timeline__chapter-expand-icon" />
                    ) : (
                      <ExpandMoreIcon size={24} color="rgb(255, 255, 255)" className="timeline__chapter-expand-icon" />
                    )}
                  </>
                )}
                {chapter.isLocked && (
                  <LockIcon size={22} color="rgb(255, 255, 255)" className="timeline__chapter-lock-icon" />
                )}
                <p
                  className="timeline__chapter-title"
                >
                  {chapter.title}
                </p>

                {/* Chapter Progress Indicator */}
                <Box className="timeline__chapter-progress">
                  <p
                    className="timeline__chapter-progress-text"
                  >
                    {chapter.completedCount}/{chapter.totalCount}
                  </p>
                  {isChapterComplete ? (
                    <Box className="timeline__item-icon timeline__item-icon--completed">
                      <CheckIcon size={14} color="var(--brand-on-primary)" strokeWidth={3} />
                    </Box>
                  ) : (
                    <Box className="timeline__chapter-progress-circle">
                      <CircularProgress
                        variant="determinate"
                        value={chapterProgress}
                        size={20}
                        thickness={6}
                        className="timeline__chapter-progress-indicator"
                      />
                      <CircleIcon
                        size={20}
                        color="rgba(255, 255, 255, 0.3)"
                        className="timeline__chapter-progress-background"
                      />
                    </Box>
                  )}
                </Box>


              </Box>

              {/* Chapter Items */}
              {isExpanded && chapter.items.length > 0 && (
                <Box className="timeline__items">
                  {chapter.items.map((item) => (
                    <Box
                      key={item.id}
                      onClick={() => !item.isLocked && onItemClick?.(item.id, chapter.id)}
                      className={`timeline__item ${item.isActive ? 'timeline__item--active' : ''} ${item.isLocked ? 'timeline__item--locked' : ''}`}
                    >
                      {/* Content */}
                      <Box className="timeline__item-content">
                        <p
                          className={`timeline__item-title ${item.isActive ? 'timeline__item-title--active' : ''} ${item.isLocked ? 'timeline__item-title--locked' : ''}`}
                        >
                          <span className="timeline__item-title-icon">
                            {getItemTypeIcon(item.type, item.isActive ? 'var(--brand-primary)' : 'rgba(255, 255, 255, 0.5)')}
                          </span>
                          {getItemLabel(item)}
                        </p>
                        <p className="timeline__item-bigtitle">
                          {item.title || item.description || ''}
                        </p>
                      </Box>
                      {/* Icon */}
                      <Box className="timeline__item-icon-container">
                        {getItemIcon(item)}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Chapter Info - Deadline */}
              {chapter.deadline && chapter.remainingCount !== undefined && chapter.remainingCount > 0 && (
                <Box className="timeline__chapter-deadline-message">
                  <WarningIcon size={16} color="#FF0000" />
                  <Typography variant="body2" className="timeline__chapter-deadline-text">
                    {chapter.remainingCount === 1
                      ? t('timeline.chapterDeadlineSingular', { count: chapter.remainingCount, date: formatDeadline(chapter.deadline) })
                      : t('timeline.chapterDeadlinePlural', { count: chapter.remainingCount, date: formatDeadline(chapter.deadline) })
                    }
                  </Typography>
                </Box>
              )}

              {chapter.isLocked && chapter.unlockDate && (
                <Box className="timeline__chapter-unlock-message">
                  <InfoIcon size={16} color="#A0A0A0" />
                  <Typography variant="body2" className="timeline__chapter-unlock-text">
                    Chapitre accessible dès le {formatUnlockDate(chapter.unlockDate)}.
                  </Typography>
                </Box>
              )}
            </div>
          );
        })}

        {/* Étape mesure d'impact (après les chapitres) */}
        {assessmentFinal && renderAssessmentStep(assessmentFinal)}
        {assessmentFollowups.map((step: AssessmentTimelineStep) => (
          <React.Fragment key={step.followupId || step.title}>
            {renderAssessmentStep(step)}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default Timeline;

